<template>
    <div style="display: inline;">
        <slot></slot>
    </div>
</template>

<script setup>
import { merge } from "@/utils/tools";
import { Manager } from "./manager";
import { requireLoader } from "@jupyter-widgets/html-manager";
import {
    WIDGET_MIMETYPE,
    WidgetRenderer
} from "@jupyter-widgets/html-manager/lib/output_renderers";
import { MathJaxTypesetter } from "@jupyterlab/mathjax2";
import { OutputArea, OutputAreaModel } from "@jupyterlab/outputarea";
import { RenderMimeRegistry } from "@jupyterlab/rendermime";
import { Widget } from "@phosphor/widgets";
import { getRenderers, requestKernel } from "./util";
// 引入全局实例
import _CodeMirror from 'codemirror/lib/codemirror'
// 核心样式
import 'codemirror/lib/codemirror.css'
// 主题样式
import 'codemirror/addon/hint/show-hint.css'

import { defineProps, onMounted, defineExpose, ref, defineEmits } from 'vue'

import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/mode/python/python.js';
import "codemirror/addon/edit/closebrackets"; //自动闭合符号
import "codemirror/addon/hint/show-hint";
import "codemirror/keymap/sublime";
import "codemirror/theme/seti.css";

import "codemirror/addon/scroll/simplescrollbars.js"
import "codemirror/addon/scroll/simplescrollbars.css"

import { useStore } from "vuex";

const store = useStore();

// 获取全局实例
const CodeMirror = window.CodeMirror || _CodeMirror;
let coder = null; // 编辑器实例

const props = defineProps({
    ops: {
        type: Object,
        default() {
            return {}
        }
    },
    isHorizontal: {
        type: Boolean,
        default: false
    }
})

const emits = defineEmits(['change-status'])

// 初始化
const initialize = () => {
    coder = new CodeMirror(document.querySelector("#" + props.ops.codeRef), mergedOptions.value.codeMirrorConfig)
    //  关键字代码提示
    coder.setOption("extraKeys", {
        Tab: function (cm) {
            let spaces = Array(cm.getOption("indentUnit") + 1).join(
                " "
            );
            cm.replaceSelection(spaces);
        }
    });
    coder.on("keypress", () => {
        coder.showHint();
    });
}


let kernel = ref(null);
let sessionPromise = ref(null);
let kernelPromise = ref(null);
let outputArea = ref(null);
let mergedOptions = ref({});

let option = ref({
    codeRef: "codeArea",
    codeLeftRef: "codeLeftArea",
    outputAreaRef: "outputArea",
    mathjaxUrl:
        "https://cdn.bootcdn.net/ajax/libs/mathjax/3.2.0/es5/latest.min.js\n",
    mathjaxConfig: "TeX-AMS_CHTML-full,Safe",
    kernelOptions: {
        name: "python3",
        path: "",
        serverSettings: {
            baseUrl: "https://notebook.greedyai.com:8888",
            token: "TXKJ_ShiminghuiZhaolinjie!"
        }
    },
    codeMirrorConfig: {
        theme: "seti",
        lineNumbers: true,
        firstLineNumber: 1,
        scrollbarStyle: "simple",
        indentUnit: 4, // 写代码回车4个空格
        autoCloseBrackets: true, //自动闭合符号
        hintOptions: {
            completeSingle: false
        }
    }
})

const bootstrap = () => {
    mergedOptions.value = merge(option.value, props.ops);
    mergedOptions.value.manager = new Manager({
        loader: requireLoader
    });
    // 渲染编辑器
    _renderCell(mergedOptions.value);
}

const _renderCell = (options) => {
    mergedOptions.value = merge({}, options);
    let renderers = {
        initialFactories: getRenderers(mergedOptions.value)
    };
    if (mergedOptions.value.mathjaxUrl) {
        renderers.latexTypesetter = new MathJaxTypesetter({
            url: mergedOptions.value.mathjaxUrl,
            config: mergedOptions.value.mathjaxConfig
        });
    }
    let renderMime = new RenderMimeRegistry(renderers);

    let manager = options.manager;

    renderMime.addFactory({
        safe: false,
        mimeTypes: [WIDGET_MIMETYPE],
        createRenderer: options => new WidgetRenderer(options, manager)
    });
    let model = new OutputAreaModel({ trusted: true });

    outputArea.value = new OutputArea({
        model: model,
        rendermime: renderMime
    });
    // 所有编辑器共用一个kernel
    if (window.kernelPromise) {
        kernelPromise.value = window.kernelPromise;
    } else {
        kernelPromise.value = new Promise((resolve, reject) => {
            // TODO 可以把kernel相关的js抽离出一个单独的文件
            window.kernelResolve = resolve;
            window.kernelReject = reject;
        });
        window.kernelPromise = kernelPromise.value;
    }
    kernelPromise.value.then(kernelItem => {
        window.kernel = kernelItem; // 对应94行判断
        kernel.value = kernelItem;
        manager.registerWithKernel(kernelItem);
        return kernelItem;
    });
    // 设置输出区域
    Widget.attach(
        outputArea.value,
        document.querySelector("#" + mergedOptions.value.outputAreaRef)
    );
    // 初始化编辑器
    //   this._initCodeMirror();

}

const execute = (code) => {
    // let code = this.codeMirror.getValue()
    if (!(kernel.value = window.kernel)) {
        // eslint-disable-next-line
        console.error("No kernel connected");
        outputArea.value.model.clear();
        // this.outputArea.model.add({
        //     output_type: "stream",
        //     name: "stdout",
        //     text: "Waiting for kernel...",
        // })
        sessionPromise.value = _getKernel();
        sessionPromise.value.then(session => {
            window.kernelResolve(session.kernel);
        });
    }
    kernelPromise.value.then(kernel => {
        outputArea.value.future = kernel.requestExecute({ code: code });
        // Confirm whether the program is completed?
        outputArea.value.future.done.then(reply => {
            store.commit(
                "changeCodeRunStatus",
                reply.content.status
            );
            emits('change-status', reply.content.status);
        });
        if (kernel["_status"] === "busy") {
            interrupt();
        }
    });
    return false;
}

const _getKernel = () => {
    return requestKernel(mergedOptions.value.kernelOptions);
}

// 清空编辑器输出区域
const refreshOutputArea = () => {
    outputArea.value.model.clear();
}
const interrupt = () => {
    if (kernel.value) {
        kernel.value.interrupt();
    }
}
const restart = () => {
    refreshOutputArea();
    if (kernel.value) {
        kernel.value.restart();
    }
}

const getCodeMirror = () => {
    return coder;
}

defineExpose({
    getCodeMirror,
    execute
})

onMounted(() => {
    bootstrap();
    initialize();
})

</script>

<style lang="less">
.CodeMirror {
    .CodeMirror-code {
        font-size: 16px;
        line-height: 24px;
    }

    .CodeMirror-scroll {
        height: 240px;
        overflow-y: auto !important;
        overflow-x: hidden !important;
    }

    .CodeMirror-simplescroll-horizontal div,
    .CodeMirror-simplescroll-vertical div {
        background: #717173;
        border: none;
        border-radius: 4px;
    }

    .CodeMirror-simplescroll-horizontal,
    .CodeMirror-simplescroll-vertical {
        background: transparent;
    }

    .CodeMirror-simplescroll-vertical {
        width: 6px;
    }
}

.answer-code{
    .CodeMirror {
        .CodeMirror-scroll {
            height: 400px;
        }
    }
}
</style>
